import { For, createEffect, onCleanup, onMount } from "solid-js";
import { colorFromPickerPoint, hslToHex, normalizeHue } from "../color";
import { styles } from "../styles";
import type { HslColorFilter } from "../types";

type Props = {
  value: HslColorFilter;
  onChange: (value: HslColorFilter) => void;
};

type Point = { x: number; y: number };

const presets: Array<{ label: string; color: HslColorFilter }> = [
  { label: "黒", color: { hue: 0, saturation: 0, lightness: 5 } },
  { label: "白", color: { hue: 0, saturation: 0, lightness: 95 } },
  { label: "グレー", color: { hue: 0, saturation: 0, lightness: 50 } },
  { label: "赤", color: { hue: 0, saturation: 80, lightness: 50 } },
  { label: "橙", color: { hue: 30, saturation: 85, lightness: 50 } },
  { label: "黄", color: { hue: 60, saturation: 85, lightness: 50 } },
  { label: "緑", color: { hue: 120, saturation: 70, lightness: 42 } },
  { label: "青緑", color: { hue: 180, saturation: 70, lightness: 45 } },
  { label: "青", color: { hue: 220, saturation: 80, lightness: 50 } },
  { label: "紫", color: { hue: 280, saturation: 70, lightness: 50 } },
  { label: "ピンク", color: { hue: 330, saturation: 75, lightness: 65 } },
];

export function HslColorPicker(props: Props) {
  let wheel!: HTMLDivElement;
  let cursor!: HTMLSpanElement;
  let activePointer: number | undefined;
  let dragRect: DOMRect | undefined;
  let queuedPoint: Point | undefined;
  let animationFrame: number | undefined;

  const positionCursor = (hue: number, saturation: number, size = wheel.clientWidth) => {
    const angle = hue * Math.PI / 180;
    const radius = saturation / 100 * size / 2;
    const x = size / 2 + Math.cos(angle) * radius;
    const y = size / 2 + Math.sin(angle) * radius;
    cursor.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
  };

  const applyPoint = (point: Point) => {
    const rect = dragRect ?? wheel.getBoundingClientRect();
    const color = colorFromPickerPoint(point, rect);

    // Update the cursor immediately; reactive consumers update independently.
    positionCursor(color.hue, color.saturation, rect.width);
    props.onChange({ ...props.value, ...color });
  };

  const flushQueuedPoint = () => {
    animationFrame = undefined;
    const point = queuedPoint;
    queuedPoint = undefined;
    if (point) applyPoint(point);
  };

  const queuePoint = (event: PointerEvent) => {
    // Use the latest high-frequency sample and paint at most once per frame.
    const samples = event.getCoalescedEvents?.() ?? [];
    const latest = samples.at(-1) ?? event;
    queuedPoint = { x: latest.clientX, y: latest.clientY };
    if (animationFrame === undefined) animationFrame = requestAnimationFrame(flushQueuedPoint);
  };

  const finishDragging = (event: PointerEvent) => {
    if (event.pointerId !== activePointer) return;

    queuedPoint = { x: event.clientX, y: event.clientY };
    if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    flushQueuedPoint();

    if (wheel.hasPointerCapture(event.pointerId)) wheel.releasePointerCapture(event.pointerId);
    activePointer = undefined;
    dragRect = undefined;
  };

  onMount(() => positionCursor(props.value.hue, props.value.saturation));

  createEffect(() => {
    const { hue, saturation } = props.value;
    if (activePointer === undefined) positionCursor(hue, saturation);
  });

  onCleanup(() => {
    if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
  });

  const shadeColor = () => props.value.lightness < 50 ? "#000" : "#fff";
  const shadeOpacity = () => Math.abs(props.value.lightness - 50) / 50;

  return (
    <div class={styles.hslPicker}>
      <div class={styles.presetColors} aria-label="代表色">
        <For each={presets}>{(preset) => (
          <button
            class={styles.presetColor}
            classList={{ [styles.presetColorSelected]: hslToHex(props.value) === hslToHex(preset.color) }}
            type="button"
            title={preset.label}
            aria-label={`${preset.label}を選択`}
            style={{ "background-color": hslToHex(preset.color) }}
            onClick={() => props.onChange({ ...preset.color })}
          />
        )}</For>
      </div>
      <div class={styles.hslPickerMain}>
        <label class={styles.lightnessControl}>
          <input
            type="range"
            min="0"
            max="100"
            value={props.value.lightness}
            aria-label="明度"
            style={{
              background: `linear-gradient(to bottom, #fff, hsl(${props.value.hue} ${props.value.saturation}% 50%), #000)`,
            }}
            onInput={(event) => props.onChange({ ...props.value, lightness: event.currentTarget.valueAsNumber })}
          />
        </label>
        <div class={styles.hslWheelWrap}>
          <div
            ref={wheel}
            class={styles.hslWheel}
            role="slider"
            tabIndex={0}
            aria-label="色相と彩度"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(props.value.saturation)}
            aria-valuetext={`色相 ${Math.round(props.value.hue)}度、彩度 ${Math.round(props.value.saturation)}%`}
            onPointerDown={(event) => {
              if (!event.isPrimary || event.button !== 0 || activePointer !== undefined) return;
              event.preventDefault();
              activePointer = event.pointerId;
              dragRect = wheel.getBoundingClientRect();
              wheel.setPointerCapture(event.pointerId);
              applyPoint({ x: event.clientX, y: event.clientY });
            }}
            onPointerMove={(event) => {
              if (event.pointerId !== activePointer) return;
              event.preventDefault();
              queuePoint(event);
            }}
            onPointerUp={finishDragging}
            onPointerCancel={finishDragging}
            onLostPointerCapture={() => {
              activePointer = undefined;
              dragRect = undefined;
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                props.onChange({ ...props.value, hue: normalizeHue(props.value.hue + (event.key === "ArrowLeft" ? -2 : 2)) });
              }
              if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                event.preventDefault();
                props.onChange({ ...props.value, saturation: Math.max(0, Math.min(100, props.value.saturation + (event.key === "ArrowUp" ? 2 : -2))) });
              }
            }}
          />
          <span class={styles.hslWheelShade} style={{ "background-color": shadeColor(), opacity: shadeOpacity() }} />
          <span ref={cursor} class={styles.hslWheelCursor} style={{ "background-color": hslToHex(props.value) }} />
        </div>
      </div>
    </div>
  );
}
